import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import './PageAccessManager.css';
import {
  baselineOwnerEmailsForRule,
  buildOwnerEmailSet,
  guardEmailsTextChange,
  guardRoleToggle,
  validateOwnerPermissionRules,
} from '../utils/ownerPermissionGuard';

const ROLES = [
  'Student',
  'Instructor',
  'Admin',
  'Coordinator',
  'Registrar',
  'Guest',
  'Super - Admin',
  'System Admin',
  'Super - System Admin',
  'Owner',
];
const CONDITION_OPTIONS = [
  'roles',
  'schools',
  'emails',
  'accounts',
  'roles AND schools',
  'roles OR schools',
  'roles AND emails',
  'roles OR emails',
];
const PAGE_CONFIGURATION_CONDITION_OPTIONS = [
  'roles AND schools AND emails',
  'roles AND schools AND associations',
  'roles AND schools AND emails AND associations',
  'roles AND associations',
  'roles AND emails AND associations',
];

const HOME_WIDGETS_RESOURCE = 'home.widgets';

const KNOWN_PAGE_CONFIGURATION_RESOURCES = {
  [HOME_WIDGETS_RESOURCE]: {
    label: 'Home page quick links',
    description:
      'Tiles shown on the signed-in home page (and the header menu). Narrower audiences override broader defaults.',
    metadataKey: 'widgets',
    itemNoun: 'link',
  },
};

const KNOWN_CAPABILITY_RESOURCES = [
  'newsletter.ViewAllAccounts',
  'newsletter.ViewStudentsFilter',
  'taxonomy.CreateConcentration',
  'taxonomy.CreateCoreSubjects',
  'taxonomy.EditConcentrations',
  'taxonomy.EditCoreSubjects',
  'taxonomy.EditStandardsSkillsObjectives',
  'taxonomy.CreateStandardsSkillsObjectives',
  'my-courses-manager.OpenCourseBuilder',
  'my-courses-manager.DeleteCourse',
  'course-builder.ManageCourseSubjects',
  'course-builder.EditSharedCourseContent',
  'course-builder.UpdateCreator',
  'course-builder.ModlockControl',
  'course-builder.ComponentNotificationSettings',
  'community.CreateUser',
  'community.AddStudentEnrollment',
  'login.BlockResetPassword',
  'my-events.OpenSAFForm',
  'my-events.ViewAllEvents',
  'my-events.ViewRegistrations',
  'my-events.DownloadRegistrations',
  'my-events.DownloadDocuments',
  'saf-form.MayViewSAF',
  'saf-form.MayEditSAF',
  'assignment-builder.UseAssistant',
  'assignment-builder.GenerateDraft',
  'assignment-builder.CreateTaxonomy',
  'course-builder.UseAssistant',
  'course-builder.GenerateOutline',
  'course-builder.GenerateAssignmentDraft',
];

const CAPABILITY_LABELS = {
  'newsletter.ViewAllAccounts': 'View all accounts',
  'newsletter.ViewStudentsFilter': 'Students filter (period / association)',
  'taxonomy.CreateConcentration': 'Create CTE pathways',
  'taxonomy.CreateCoreSubjects': 'Create core subjects',
  'taxonomy.EditConcentrations': 'Edit CTE pathways',
  'taxonomy.EditCoreSubjects': 'Edit core subjects',
  'taxonomy.EditStandardsSkillsObjectives': 'Edit standards, skills, objectives',
  'taxonomy.CreateStandardsSkillsObjectives': 'Create standards, skills, objectives',
  'my-courses-manager.OpenCourseBuilder': 'Open in Course Builder (flagged courses panel)',
  'my-courses-manager.DeleteCourse': 'Delete course (double-click red × in course row; soft-deletes by setting status to Deleted)',
  'course-builder.ManageCourseSubjects': 'Course Subject Manager (gear on General courses)',
  'course-builder.EditSharedCourseContent': 'Edit shared course content (components, assignments, discussion threads)',
  'course-builder.UpdateCreator': 'Update course creator (transfer ownership from Course Builder)',
  'course-builder.ModlockControl': 'Course Builder: toggle course modlock (enrollment lock)',
  'course-builder.ComponentNotificationSettings':
    'Course Builder: LMS completion email targets (bulk 📧 next to Deployment Flag; components.notification_settings)',
  'community.CreateUser': 'Community: create new user (FAB + account form)',
  'community.AddStudentEnrollment': 'Community: add enrollment on student access cards',
  'login.BlockResetPassword':
    'Block self-service password reset for these audiences. Rules describe who is BLOCKED; a user keeps reset access as long as any one of their active access cards is not matched. No rule = everyone can reset.',
  'my-events.OpenSAFForm':
    'Show the open-SAF icon on Field Experience Events. Opens the form in a new tab; view vs edit is enforced on the SAF Form page.',
  'my-events.ViewAllEvents':
    'See field experience events beyond personal invitations — all events for global access cards, or school-scoped events for school access cards. Replaces the former Admin-only expanded list.',
  'my-events.ViewRegistrations':
    'Show the registrations list icon on Field Experience Events. Opens a slide-up panel with student registrations for that event.',
  'my-events.DownloadRegistrations':
    'Show the bulk download control on Field Experience Events. Export event summaries or student registrations as CSV, and open Present mode.',
  'my-events.DownloadDocuments':
    'Show the documents library (folder icon) on Field Experience Events. Full-screen browser for event fliers and hour verification PDFs with bulk download.',
  'saf-form.MayViewSAF':
    'View SAF forms for in-scope events without a coordinator invitation (read-only on saf/official stages). Enforced server-side on load.',
  'saf-form.MayEditSAF':
    'Edit SAF forms for in-scope events without a coordinator invitation (includes approved request stage). Enforced server-side on load and save.',
  'assignment-builder.UseAssistant':
    'Show and use the Assignment Builder AI assistant (chat bubble). Requires an explicit Page Access Manager rule.',
  'assignment-builder.GenerateDraft':
    'Generate assignment drafts via the assistant. Requires an explicit Page Access Manager rule.',
  'assignment-builder.CreateTaxonomy':
    'Allow the assistant to propose new taxonomy items and create them after instructor review (also requires taxonomy create capability).',
  'course-builder.UseAssistant':
    'Show and use the Course Builder AI assistant (chat bubble). Requires an explicit Page Access Manager rule.',
  'course-builder.GenerateOutline':
    'Generate full course outlines (modules, lessons, content, assignments, discussions). Requires an explicit Page Access Manager rule.',
  'course-builder.GenerateAssignmentDraft':
    'After inserting a course outline, batch-generate assignment question drafts for AI placeholder slots (also works with assignment-builder Generate draft).',
};

const KNOWN_PAGE_PATHS = [
  'home',
  'admin-directory', 'all-hours', 'assignment', 'assignment-builder',
  'page-access-manager',
  'assignment-submissions-auditor', 'community', 'componentsubmissions', 'course-builder',
  'course-enroll-manager', 'course-viewer', 'courses-previewer', 'create-field-experience',
  'dashboard', 'email-templator', 'fe-event-attendance', 'relationships',
  'modify-pdf', 'my-courses', 'my-courses-manager', 'my-directory', 'my-events', 'my-hours',
  'newsletter', 'newsletter-viewer', 'project-builder', 'projects-directory',
  'register-for-field-experience', 'register-a-student-for-an-event', 'request-fe-hours-by-external-activity',
  'saf-form',
  'schedule-builder',
  'user-management',
  'users-csv-import', 'waiver-forms', 'taxonomy', 'sms-notifications',
  'open-fe-event-survey', 'action', 'open-registration-matcher',
  'login',
];

const DEFAULT_PAGE_RULES = [
  { condition: 'roles', roles: ['Admin', 'Super - Admin', 'System Admin', 'Super - System Admin'] },
];

function capabilityLabel(resource) {
  return CAPABILITY_LABELS[resource] || resource.split('.').pop().replace(/([A-Z])/g, ' $1').trim();
}

// Friendly names so the list reads as pages, not raw routes.
const PAGE_NAME_OVERRIDES = {
  home: 'Home',
  'saf-form': 'SAF Form',
  'fe-event-attendance': 'FE Event Attendance',
  'request-fe-hours-by-external-activity': 'Request FE Hours (External Activity)',
  'register-for-field-experience': 'Register for Field Experience',
  'open-fe-event-survey': 'Open FE Event Survey',
  'sms-notifications': 'SMS Notifications',
  'modify-pdf': 'Modify PDF',
  'users-csv-import': 'Users CSV Import',
  'email-templator': 'Email Templator',
};

/** What each route is for — shown in the bottom panel when a page widget is selected. */
const PAGE_DESCRIPTIONS = {
  home: 'Signed-in landing page with quick-link tiles and shortcuts to common tasks.',
  'admin-directory': 'My Directory but for all instructors',
  'all-hours': 'Overview of student field experience hours for review and auditing.',
  assignment: 'View a single assignment (content, submission, and grading).',
  'assignment-builder': 'Create and edit assignments, rubrics, and delivery settings.',
  'page-access-manager': 'Configure who can open routes and use gated features across the app.',
  'assignment-submissions-auditor': 'IN DEVELOPMENT -- attempting to find assignmentsubmissions that did not receive updated hourawards and where the questions match the updated questions.',
  community: 'Community roster — users, enrollments, and student access cards.',
  componentsubmissions: 'Review LMS component submissions across courses.',
  'course-builder': 'Author courses: sections, components, assignments, and deployment flags.',
  'course-enroll-manager': 'Manage course enrollments and roster placement.',
  'course-viewer': 'Read and complete course content as a student or instructor.',
  'courses-previewer': 'Admin preview and browse all courses in the system.',
  'create-field-experience': 'Create and configure field experience (FE) events.',
  dashboard: 'View access cards and enrollments for the signed-in user.',
  'email-templator': 'DEPRECATED -- TEMPORARILY -- Design and maintain email templates for system messages.',
  'fe-event-attendance': 'Record and review attendance for field experience events.',
  relationships: 'Staff relationships to CTE pathways and concentrations (instructor assignments).',
  'modify-pdf': 'Upload and apply modifications to PDF documents.',
  'my-courses': 'Courses the signed-in user is enrolled in or teaching.',
  'my-courses-manager': 'Instructor/admin course list with flags, status, and course actions.',
  'my-directory': 'View own assignments and projects for the Projects Directory',
  'my-events': 'Field experience events the user is involved in through training plan or coordinator invitations. ViewAllEvents expands the event list (global or school scope). OpenSAFForm controls the open-SAF icon; ViewRegistrations controls the per-event registrations list; DownloadRegistrations controls bulk CSV export and Present; DownloadDocuments controls the fliers and verification PDF library.',
  'my-hours': 'Student view of their hours and submissions.',
  newsletter: 'Compose and send newsletters to selected audiences.',
  'newsletter-viewer': 'Read published newsletters.',
  'project-builder': 'Create and edit own Projects (for Projects Directory)',
  'projects-directory': 'Browse and open the projects catalog.',
  'register-for-field-experience': 'Self-service registration for field experience events.',
  'register-a-student-for-an-event': 'Staff workflow to register a student for an FE event.',
  'request-fe-hours-by-external-activity': 'Request FE hour credit for approved external activities.',
  'saf-form':
    'Schedule Approval Form for a field experience event. MayViewSAF / MayEditSAF gate load and save on the form (separate from My Events open-SAF icon rules).',
  'schedule-builder': 'IN DEVELOPMENT -- for the SLA packets workflow',
  'user-management': 'Administration of accounts, roles, schools, and access cards.',
  'users-csv-import': 'Bulk create or update user accounts from a CSV file.',
  'waiver-forms': 'List out the Waiver Forms that users are involved in',
  taxonomy: 'CTE taxonomy: pathways, core subjects, standards, skills, and objectives.',
  'sms-notifications': 'Configure and send SMS notifications.',
  'open-fe-event-survey': 'DEPRECATED -- TEMPORARILY -- Sign up for events without accounts',
  action: 'Handles tokenized action links from emails (deep links into the app).',
  'open-registration-matcher': 'DEPRECATED -- TEMPORARILY -- Match unmatched non logged in registrations to now account made students',
  login: 'Sign-in page. Route rules gate access; feature rules can block self-service password reset.',
};

const PAGE_NAME_ACRONYMS = new Set([
  'saf', 'fe', 'pdf', 'csv', 'sms', 'lms', 'cte', 'id', 'url', 'faq', 'pam',
]);

function pageStatsForKey(pageKey, permissions, homeWidgetRows) {
  const pagePerm = permissions.find((p) => p.type === 'page' && p.resource === pageKey);
  const capsKnown = knownCapabilitiesForPage(pageKey);
  const capRows = capabilityRowsForPage(pageKey, permissions);
  const customCapRows = capRows.filter((p) => !capsKnown.includes(p.resource));
  const featureCount = capsKnown.length + customCapRows.length;
  const isHome = pageKey === 'home';
  const configsForPage = isHome
    ? homeWidgetRows
    : permissions.filter(
        (p) => p.type === 'page_configuration' && p.resource.startsWith(`${pageKey}.`)
      );
  const audienceCount = isHome ? configsForPage.length : 0;
  const routeRestricted = !!(pagePerm && (pagePerm.rules || []).length);
  const ruleCount = (pagePerm ? 1 : 0) + capRows.length + configsForPage.length;
  return {
    pagePerm,
    capsKnown,
    capRows,
    customCapRows,
    featureCount,
    configsForPage,
    audienceCount,
    routeRestricted,
    ruleCount,
    showConfigs: isHome,
  };
}

function pageWidgetMetaLine({ ruleCount, routeRestricted, featureCount, audienceCount }) {
  if (ruleCount === 0) return routeRestricted ? 'Restricted' : 'Open route';
  const parts = [`${ruleCount} rule${ruleCount === 1 ? '' : 's'}`];
  if (featureCount > 0) parts.push(`${featureCount} feat`);
  if (audienceCount > 0) parts.push(`${audienceCount} aud`);
  return parts.join(' · ');
}

function pageDisplayName(pageKey) {
  if (PAGE_NAME_OVERRIDES[pageKey]) return PAGE_NAME_OVERRIDES[pageKey];
  return String(pageKey)
    .split(/[-/]/)
    .filter(Boolean)
    .map((w) =>
      PAGE_NAME_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
}

function pageDescription(pageKey) {
  if (PAGE_DESCRIPTIONS[pageKey]) return PAGE_DESCRIPTIONS[pageKey];
  return `Application route at /${pageKey}. Restrict who can load this page and any related feature rules below.`;
}

function getPagePath(pageKey) {
  if (pageKey === 'home') return '/';
  return `/${pageKey}`;
}

function knownCapabilitiesForPage(pageKey) {
  return KNOWN_CAPABILITY_RESOURCES.filter((r) => r.startsWith(`${pageKey}.`));
}

function capabilityRowsForPage(pageKey, permissions) {
  const capsKnown = knownCapabilitiesForPage(pageKey);
  return permissions.filter(
    (p) => p.type === 'capability' && capsKnown.includes(p.resource)
  );
}

function summarizeRule(r, schoolById, associations = []) {
  const cond = (r.condition || '').trim() || 'rule';
  const bits = [];

  if (cond.includes('roles')) {
    if ((r.roles || []).includes('any')) bits.push('roles: any');
    else if (r.roles?.length) bits.push(`roles: ${r.roles.join(', ')}`);
    else bits.push('roles: (none selected)');
  }
  if (cond.includes('schools')) {
    if ((r.schools || []).includes('any')) bits.push('schools: any');
    else if (r.schools?.length) {
      const names = r.schools.map((id) => schoolById[String(id)] || id);
      bits.push(`schools: ${names.join(', ')}`);
    } else bits.push('schools: (none selected)');
  }
  if (cond.includes('emails')) {
    if (r.emails?.length) bits.push(`emails: ${r.emails.join(', ')}`);
    else bits.push('emails: (none)');
  }
  if (cond.includes('accounts')) {
    if (r.accounts?.length) bits.push(`accounts: ${r.accounts.join(', ')}`);
    else bits.push('accounts: (none)');
  }
  if (cond.includes('associations')) {
    if ((r.associations || []).includes('any')) bits.push('associations: any');
    else if (r.associations?.length) {
      const names = r.associations.map((id) => {
        const a = associations.find((x) => String(x.id) === String(id));
        return a ? a.title || a.displayname || id : id;
      });
      bits.push(`associations: ${names.join(', ')}`);
    } else bits.push('associations: (none)');
  }

  return bits.length ? `${cond} — ${bits.join('; ')}` : cond;
}

function summarizeRules(rules, schoolById, associations = []) {
  if (!rules?.length) return 'No rules yet';
  return rules.map((r) => summarizeRule(r, schoolById, associations)).join(' · ');
}

/** Split pasted or typed email lists: comma, newline, or semicolon. */
function parseEmailList(raw) {
  if (raw == null || typeof raw !== 'string') return [];
  return raw
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function emailsToTextareaValue(emails) {
  return (emails || []).join('\n');
}

/** Build rules for API: drop UI-only `emailsText` and derive `emails` when needed. */
function rulesForApi(rules) {
  return (rules || []).map((r) => {
    const { emailsText, ...rest } = r;
    if (rest.condition?.includes('emails')) {
      const raw = emailsText != null ? emailsText : emailsToTextareaValue(rest.emails);
      return { ...rest, emails: parseEmailList(raw) };
    }
    return rest;
  });
}

function cloneRules(rules) {
  return JSON.parse(JSON.stringify(rules || []));
}

function safeParseRules(raw) {
  if (typeof raw !== 'string') return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function normalizeMetadataIn(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
}

function collectPageKeys(permissions) {
  // Always surface 'home' so admins can manage its quick-link audiences even before
  // any route rule exists. Other pages appear only once they have a row.
  const set = new Set(['home']);
  permissions.forEach((p) => {
    if (p.type === 'page') set.add(p.resource);
    if (p.type === 'capability' || p.type === 'page_configuration') {
      const i = p.resource.indexOf('.');
      if (i > 0) set.add(p.resource.slice(0, i));
    }
  });
  return [...set].sort();
}

// ── Icons (tiny inline lucide-style strokes, sized 14px) ────────────────────
const IconRoute = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="6" cy="19" r="2.5" />
    <circle cx="18" cy="5" r="2.5" />
    <path d="M8.5 19H14a4 4 0 0 0 0-8H10a4 4 0 0 1 0-8h5.5" />
  </svg>
);

const IconFeature = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 7h10" />
    <path d="M4 12h16" />
    <path d="M4 17h7" />
    <circle cx="17" cy="7" r="2" fill="currentColor" stroke="none" />
    <circle cx="14" cy="17" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const IconAudience = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="9" cy="8" r="3" />
    <circle cx="17" cy="9.5" r="2.2" />
    <path d="M3.5 19c.7-3 3-4.5 5.5-4.5s4.8 1.5 5.5 4.5" />
    <path d="M15 14.5c1.6.2 3 1 4 2.5" />
  </svg>
);

const IconPageOpen = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

function RulesEditor({
  draft,
  setDraft,
  schools,
  associations = [],
  viewerIsOwner,
  ownerEmails,
  onOwnerGuardError,
}) {
  const ownerEmailSet = useMemo(() => buildOwnerEmailSet(ownerEmails), [ownerEmails]);
  const isPageConfiguration = draft.type === 'page_configuration';
  const conditionSelectOptions = isPageConfiguration
    ? [...CONDITION_OPTIONS, ...PAGE_CONFIGURATION_CONDITION_OPTIONS]
    : CONDITION_OPTIONS;

  const updateRule = useCallback(
    (ruleIndex, field, value) => {
      setDraft((d) => {
        const rules = [...(d.rules || [])];
        if (!rules[ruleIndex]) rules[ruleIndex] = { condition: 'roles', roles: [] };
        rules[ruleIndex] = { ...rules[ruleIndex], [field]: value };
        return { ...d, rules };
      });
    },
    [setDraft]
  );

  const addRule = useCallback(() => {
    setDraft((d) => {
      const newRule =
        d.type === 'page_configuration'
          ? { condition: 'roles AND schools', roles: ['Student'], schools: ['any'] }
          : { condition: 'roles', roles: ['Admin'] };
      const baselineRules = d.ownerBaselineRules || cloneRules(d.rules || []);
      return {
        ...d,
        rules: [...(d.rules || []), newRule],
        ownerBaselineRules: [...baselineRules, cloneRules([newRule])[0]],
      };
    });
  }, [setDraft]);

  const removeRule = useCallback(
    (ruleIndex) => {
      setDraft((d) => ({
        ...d,
        rules: (d.rules || []).filter((_, i) => i !== ruleIndex),
        ownerBaselineRules: (d.ownerBaselineRules || []).filter((_, i) => i !== ruleIndex),
      }));
    },
    [setDraft]
  );

  return (
    <div style={s.rulesStack}>
      {(draft.rules || []).map((rule, ri) => (
        <div key={ri} style={s.ruleCard}>
          <select
            value={rule.condition || 'roles'}
            onChange={(e) => updateRule(ri, 'condition', e.target.value)}
            style={s.selectCompact}
          >
            {conditionSelectOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {rule.condition?.includes('roles') && (
            <div style={s.chipRow}>
              <label style={s.chip}>
                <input
                  type="checkbox"
                  checked={(rule.roles || []).includes('any')}
                  onChange={(e) => {
                    const next = e.target.checked ? ['any'] : (rule.roles || []).filter((r) => r !== 'any');
                    updateRule(ri, 'roles', next);
                  }}
                />
                any
              </label>
              {!(rule.roles || []).includes('any') &&
                ROLES.map((role) => {
                  const isOwnerRole = role === 'Owner';
                  const roleLocked = isOwnerRole && !viewerIsOwner;
                  return (
                  <label
                    key={role}
                    style={{
                      ...s.chip,
                      ...(roleLocked ? s.chipProtected : {}),
                    }}
                    title={roleLocked ? 'Only Owner can change the Owner role.' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={(rule.roles || []).includes(role)}
                      disabled={roleLocked}
                      onChange={(e) => {
                        const guard = guardRoleToggle(role, e.target.checked, { viewerIsOwner });
                        if (!guard.ok) {
                          onOwnerGuardError(guard.error);
                          return;
                        }
                        const roles = rule.roles || [];
                        const next = e.target.checked ? [...roles, role] : roles.filter((r) => r !== role);
                        updateRule(ri, 'roles', next);
                      }}
                    />
                    {role}
                  </label>
                  );
                })}
            </div>
          )}
          {rule.condition?.includes('schools') && (
            <div style={s.chipRow}>
              <label style={s.chip}>
                <input
                  type="checkbox"
                  checked={(rule.schools || []).includes('any')}
                  onChange={(e) => {
                    const next = e.target.checked ? ['any'] : (rule.schools || []).filter((id) => id !== 'any');
                    updateRule(ri, 'schools', next);
                  }}
                />
                any
              </label>
              {!(rule.schools || []).includes('any') &&
                schools.slice(0, 20).map((sch) => (
                  <label key={sch.school_id} style={s.chip}>
                    <input
                      type="checkbox"
                      checked={(rule.schools || []).includes(sch.school_id.toString())}
                      onChange={(e) => {
                        const schoolsList = rule.schools || [];
                        const next = e.target.checked
                          ? [...schoolsList, sch.school_id.toString()]
                          : schoolsList.filter((id) => id !== sch.school_id.toString());
                        updateRule(ri, 'schools', next);
                      }}
                    />
                    {sch.school_name}
                  </label>
                ))}
              {!(rule.schools || []).includes('any') && schools.length > 20 && (
                <span style={s.mutedSmall}>(+{schools.length - 20} schools)</span>
              )}
            </div>
          )}
          {rule.condition?.includes('emails') && (
            <>
              <textarea
                placeholder="One email per line, or separate with commas"
                value={rule.emailsText != null ? rule.emailsText : emailsToTextareaValue(rule.emails)}
                onChange={(e) => {
                  const baselineRule = (draft.ownerBaselineRules || [])[ri] || rule;
                  const guard = guardEmailsTextChange(
                    e.target.value,
                    baselineOwnerEmailsForRule(baselineRule, ownerEmails),
                    { viewerIsOwner, ownerEmails }
                  );
                  if (!guard.ok) {
                    onOwnerGuardError(guard.error);
                    return;
                  }
                  updateRule(ri, 'emailsText', guard.value);
                }}
                onPaste={(e) => {
                  if (viewerIsOwner) return;
                  const baselineRule = (draft.ownerBaselineRules || [])[ri] || rule;
                  const pasted = e.clipboardData.getData('text');
                  const current =
                    rule.emailsText != null ? rule.emailsText : emailsToTextareaValue(rule.emails);
                  const el = e.target;
                  const start = el.selectionStart ?? current.length;
                  const end = el.selectionEnd ?? current.length;
                  const next = current.slice(0, start) + pasted + current.slice(end);
                  const guard = guardEmailsTextChange(
                    next,
                    baselineOwnerEmailsForRule(baselineRule, ownerEmails),
                    { viewerIsOwner, ownerEmails }
                  );
                  if (!guard.ok) {
                    e.preventDefault();
                    onOwnerGuardError(guard.error);
                  }
                }}
                style={s.emailTextarea}
                rows={3}
                spellCheck={false}
                autoComplete="off"
              />
              {!viewerIsOwner && ownerEmailSet.size > 0 && (
                <span style={s.mutedSmall}>
                  Owner account emails in this rule cannot be added or removed unless you are Owner.
                </span>
              )}
            </>
          )}
          {rule.condition?.includes('accounts') && (
            <input
              type="text"
              placeholder="Comma-separated account IDs"
              value={(rule.accounts || []).join(', ')}
              onChange={(e) =>
                updateRule(
                  ri,
                  'accounts',
                  e.target.value.split(',').map((x) => x.trim()).filter(Boolean)
                )
              }
              style={s.inputRule}
            />
          )}
          {rule.condition?.includes('associations') && (
            <div style={s.chipRow}>
              <label style={s.chip}>
                <input
                  type="checkbox"
                  checked={(rule.associations || []).includes('any')}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? ['any']
                      : (rule.associations || []).filter((id) => id !== 'any');
                    updateRule(ri, 'associations', next);
                  }}
                />
                any
              </label>
              {!(rule.associations || []).includes('any') &&
                associations.slice(0, 40).map((assoc) => (
                  <label key={assoc.id} style={s.chip}>
                    <input
                      type="checkbox"
                      checked={(rule.associations || []).some((x) => String(x) === String(assoc.id))}
                      onChange={(e) => {
                        const list = rule.associations || [];
                        const id = String(assoc.id);
                        const next = e.target.checked
                          ? [...list.filter((x) => String(x) !== id), id]
                          : list.filter((x) => String(x) !== id);
                        updateRule(ri, 'associations', next);
                      }}
                    />
                    {assoc.title || assoc.displayname || assoc.id}
                  </label>
                ))}
              {!(rule.associations || []).includes('any') && associations.length > 40 && (
                <span style={s.mutedSmall}>(+{associations.length - 40} associations)</span>
              )}
            </div>
          )}
          <button type="button" onClick={() => removeRule(ri)} style={s.textDanger}>
            Remove rule
          </button>
        </div>
      ))}
      <button type="button" onClick={addRule} style={s.addRule}>
        + Add rule
      </button>
    </div>
  );
}

function WidgetsMetadataEditor({ draft, setDraft }) {
  const widgets = Array.isArray(draft?.metadata?.widgets) ? draft.metadata.widgets : [];

  const setWidgets = useCallback(
    (next) => {
      setDraft((d) => ({
        ...d,
        metadata: { ...(d.metadata || {}), widgets: next },
      }));
    },
    [setDraft]
  );

  const update = (i, field, value) => {
    const next = widgets.map((w, idx) => (idx === i ? { ...w, [field]: value } : w));
    setWidgets(next);
  };

  const add = () => setWidgets([...widgets, { label: '', path: '', icon: '' }]);

  const remove = (i) => setWidgets(widgets.filter((_, idx) => idx !== i));

  const move = (i, direction) => {
    const j = i + direction;
    if (j < 0 || j >= widgets.length) return;
    const next = widgets.slice();
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    setWidgets(next);
  };

  return (
    <div style={s.widgetBlock}>
      <div style={s.widgetBlockTitle}>Tiles for this audience</div>
      <p style={s.widgetHint}>
        Each entry is a tile shown on the home page (and in the header menu). Path may include query/hash. Icon is
        optional — leave blank to use a default mapped from the label.
      </p>
      {widgets.length === 0 && (
        <p style={s.mutedNote}>No tiles yet. Click "Add tile" below.</p>
      )}
      {widgets.map((w, i) => (
        <div key={i} style={s.widgetCard}>
          <div style={s.widgetTopRow}>
            <input
              type="text"
              placeholder="🏠 (optional emoji)"
              value={w.icon || ''}
              onChange={(e) => update(i, 'icon', e.target.value)}
              style={s.iconInput}
              maxLength={6}
              spellCheck={false}
            />
            <input
              type="text"
              placeholder="Label (e.g. My Hours)"
              value={w.label || ''}
              onChange={(e) => update(i, 'label', e.target.value)}
              style={s.widgetInput}
            />
            <div style={s.widgetReorder}>
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                style={s.reorderBtn}
                aria-label="Move up"
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === widgets.length - 1}
                style={s.reorderBtn}
                aria-label="Move down"
                title="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                style={s.widgetRemove}
                aria-label="Remove tile"
                title="Remove"
              >
                ×
              </button>
            </div>
          </div>
          <input
            type="text"
            placeholder="/path?with=query"
            value={w.path || ''}
            onChange={(e) => update(i, 'path', e.target.value)}
            style={s.widgetPathInput}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      ))}
      <button type="button" onClick={add} style={s.addWidgetBtn}>
        + Add tile
      </button>
    </div>
  );
}

function PagePermissionsDetail({
  pageKey,
  permissions,
  homeWidgetRows,
  schoolById,
  associations,
  hoverListRoute,
  setHoverListRoute,
  hoverListCap,
  setHoverListCap,
  openPageEditor,
  openCapabilityEditor,
  openPageConfigCreate,
  openPageConfigEditor,
}) {
  const pagePerm = permissions.find((p) => p.type === 'page' && p.resource === pageKey);
  const capsKnown = knownCapabilitiesForPage(pageKey);
  const capRows = capabilityRowsForPage(pageKey, permissions);
  const customCapRows = capRows.filter((p) => !capsKnown.includes(p.resource));
  const featureCount = capsKnown.length + customCapRows.length;
  const isHome = pageKey === 'home';
  const configsForPage = isHome
    ? homeWidgetRows
    : permissions.filter((p) => p.type === 'page_configuration' && p.resource.startsWith(`${pageKey}.`));
  const showConfigs = isHome;

  return (
    <div style={s.pageCardBody}>
      <button
        type="button"
        style={{
          ...s.rowBtn,
          ...(hoverListRoute === pageKey ? s.rowBtnHover : {}),
        }}
        onClick={() => openPageEditor(pageKey)}
        onMouseEnter={() => setHoverListRoute(pageKey)}
        onMouseLeave={() => setHoverListRoute(null)}
        title="Edit route rule"
      >
        <span style={s.rowIcon}><IconRoute /></span>
        <span style={s.rowBody}>
          <span style={s.rowEyebrow}>Route access</span>
          <span style={s.rowPreview}>
            {pagePerm
              ? summarizeRules(pagePerm.rules, schoolById, associations)
              : 'Anyone can open — no route rule'}
          </span>
        </span>
      </button>

      {featureCount > 0 && (
        <div style={s.subSection}>
          <div style={s.subSectionHead}>
            <span style={s.eyebrow}>Features</span>
            <span style={s.eyebrowCount}>{featureCount}</span>
          </div>
          {capsKnown.map((capRes) => {
            const row = capRows.find((p) => p.resource === capRes);
            return (
              <button
                key={capRes}
                type="button"
                style={{
                  ...s.rowBtn,
                  ...(hoverListCap === capRes ? s.rowBtnHover : {}),
                }}
                onClick={() => openCapabilityEditor(capRes)}
                onMouseEnter={() => setHoverListCap(capRes)}
                onMouseLeave={() => setHoverListCap(null)}
                title={capRes}
              >
                <span style={s.rowIcon}><IconFeature /></span>
                <span style={s.rowBody}>
                  <span style={s.rowTitle}>{capabilityLabel(capRes)}</span>
                  <span style={s.rowPreview}>
                    {row ? summarizeRules(row.rules, schoolById, associations) : 'Not configured'}
                  </span>
                </span>
              </button>
            );
          })}
          {customCapRows.map((p) => (
            <button
              key={p.resource}
              type="button"
              style={{
                ...s.rowBtn,
                ...(hoverListCap === p.resource ? s.rowBtnHover : {}),
              }}
              onClick={() => openCapabilityEditor(p.resource)}
              onMouseEnter={() => setHoverListCap(p.resource)}
              onMouseLeave={() => setHoverListCap(null)}
              title={p.resource}
            >
              <span style={s.rowIcon}><IconFeature /></span>
              <span style={s.rowBody}>
                <span style={s.rowTitle}>{capabilityLabel(p.resource)}</span>
                <span style={s.rowPreview}>{summarizeRules(p.rules, schoolById, associations)}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {showConfigs && (
        <div style={s.subSection}>
          <div style={s.subSectionHead}>
            <span style={s.eyebrow}>Quick links audiences</span>
            <button
              type="button"
              style={s.ghostBtn}
              onClick={() => openPageConfigCreate(HOME_WIDGETS_RESOURCE)}
              title="Add an audience for home page tiles"
            >
              + Add audience
            </button>
          </div>
          {configsForPage.length === 0 ? (
            <p style={s.subSectionHint}>No audiences yet — using the legacy role-based menu.</p>
          ) : (
            configsForPage.map((row) => {
              const tileCount = Array.isArray(row.metadata?.widgets) ? row.metadata.widgets.length : 0;
              return (
                <button
                  key={row.permission_id}
                  type="button"
                  style={{
                    ...s.rowBtn,
                    ...(hoverListCap === row.permission_id ? s.rowBtnHover : {}),
                  }}
                  onClick={() => openPageConfigEditor(row)}
                  onMouseEnter={() => setHoverListCap(row.permission_id)}
                  onMouseLeave={() => setHoverListCap(null)}
                >
                  <span style={s.rowIcon}><IconAudience /></span>
                  <span style={s.rowBody}>
                    <span style={s.rowTitle}>
                      {tileCount} {tileCount === 1 ? 'tile' : 'tiles'}
                      <span style={s.rowTitleSub}>
                        {' · '}
                        <code style={s.inlineCodeSm}>{row.resource}</code>
                      </span>
                    </span>
                    <span style={s.rowPreview}>{summarizeRules(row.rules, schoolById, associations)}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const PageAccessManager = () => {
  const [permissions, setPermissions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [viewerIsOwner, setViewerIsOwner] = useState(false);
  const [ownerEmails, setOwnerEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [customPagePath, setCustomPagePath] = useState('');
  const customRouteInputRef = useRef(null);
  const [editorModal, setEditorModal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [hoverListRoute, setHoverListRoute] = useState(null);
  const [hoverListCap, setHoverListCap] = useState(null);
  const [hoverCapRes, setHoverCapRes] = useState(null);
  const [selectedPageKey, setSelectedPageKey] = useState(null);

  const openPagePanel = useCallback((pageKey) => {
    setSelectedPageKey((prev) => (prev === pageKey ? null : pageKey));
  }, []);

  const closePagePanel = useCallback(() => {
    setSelectedPageKey(null);
  }, []);

  const schoolById = useMemo(() => {
    const m = {};
    schools.forEach((sch) => {
      m[String(sch.school_id)] = sch.school_name;
    });
    return m;
  }, [schools]);

  const homeWidgetRows = useMemo(
    () =>
      permissions
        .filter((p) => p.type === 'page_configuration' && p.resource === HOME_WIDGETS_RESOURCE)
        .sort((a, b) => String(b.updateddate || '').localeCompare(String(a.updateddate || ''))),
    [permissions]
  );

  const selectedPageStats = useMemo(
    () => (selectedPageKey ? pageStatsForKey(selectedPageKey, permissions, homeWidgetRows) : null),
    [selectedPageKey, permissions, homeWidgetRows]
  );

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const [permRes, schoolsRes, assocRes] = await Promise.all([
        axios.get('/api/permissions', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/permissions/schools', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/user-management/associations', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPermissions((permRes.data.permissions || []).map((p) => ({
        ...p,
        rules: Array.isArray(p.rules) ? p.rules : typeof p.rules === 'string' ? safeParseRules(p.rules) : [],
        metadata: normalizeMetadataIn(p.metadata),
      })));
      setViewerIsOwner(!!permRes.data.ownerContext?.viewerIsOwner);
      setOwnerEmails(permRes.data.ownerContext?.ownerEmails || []);
      setSchools(schoolsRes.data.schools || []);
      setAssociations(assocRes.data.associations || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addModalOpen) {
      // Defer to focus after render.
      setTimeout(() => customRouteInputRef.current?.focus(), 0);
    } else {
      setCustomPagePath('');
    }
  }, [addModalOpen]);

  useEffect(() => {
    if (!selectedPageKey) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !editorModal && !addModalOpen) closePagePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedPageKey, editorModal, addModalOpen, closePagePanel]);

  const pageKeys = useMemo(() => collectPageKeys(permissions), [permissions]);

  const pagesWithoutRouteRule = useMemo(
    () =>
      KNOWN_PAGE_PATHS.filter((path) => !permissions.some((p) => p.type === 'page' && p.resource === path)).sort(),
    [permissions]
  );

  const openPageEditor = (pageKey) => {
    const existing = permissions.find((p) => p.type === 'page' && p.resource === pageKey);
    if (existing) {
      const rulesArr = [...(existing.rules || [])];
      setDraft({
        ...existing,
        rules: rulesArr,
        ownerBaselineRules: cloneRules(rulesArr),
        isNew: false,
      });
    } else {
      const rulesArr = [...DEFAULT_PAGE_RULES];
      setDraft({
        type: 'page',
        resource: pageKey,
        access: true,
        rules: rulesArr,
        ownerBaselineRules: cloneRules(rulesArr),
        permission_id: null,
        isNew: true,
      });
    }
    setEditorModal({ kind: 'page', pageKey });
  };

  const openCapabilityEditor = (resource) => {
    const existing = permissions.find((p) => p.type === 'capability' && p.resource === resource);
    if (existing) {
      const rulesArr = [...(existing.rules || [])];
      setDraft({
        ...existing,
        rules: rulesArr,
        ownerBaselineRules: cloneRules(rulesArr),
        isNew: false,
      });
    } else {
      setDraft({
        type: 'capability',
        resource,
        access: true,
        rules: [],
        ownerBaselineRules: [],
        permission_id: null,
        isNew: true,
      });
    }
    setEditorModal({ kind: 'capability', resource });
  };

  const openPageConfigCreate = (resource) => {
    const rulesArr = [
      {
        condition: 'roles AND schools',
        roles: ['Student'],
        schools: ['any'],
      },
    ];
    setDraft({
      type: 'page_configuration',
      resource,
      access: true,
      rules: rulesArr,
      ownerBaselineRules: cloneRules(rulesArr),
      metadata: { widgets: [] },
      permission_id: null,
      isNew: true,
    });
    setEditorModal({ kind: 'page_configuration', resource });
  };

  const openPageConfigEditor = (row) => {
    const rulesRaw = row.rules;
    const rulesArr = Array.isArray(rulesRaw) ? rulesRaw : typeof rulesRaw === 'string' ? safeParseRules(rulesRaw) : [];
    setDraft({
      ...row,
      rules: rulesArr,
      ownerBaselineRules: cloneRules(rulesArr),
      metadata: normalizeMetadataIn(row.metadata),
      isNew: false,
    });
    setEditorModal({ kind: 'page_configuration', resource: row.resource });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditorModal(null);
    setDraft(null);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const rules = rulesForApi(draft.rules || []);
      const ownerCheck = validateOwnerPermissionRules(
        draft.isNew ? [] : draft.ownerBaselineRules || [],
        rules,
        { viewerIsOwner, ownerEmails }
      );
      if (!ownerCheck.ok) {
        setError(ownerCheck.error);
        setSaving(false);
        return;
      }
      const isPageConfig = draft.type === 'page_configuration';
      const payload = {
        type: draft.type,
        resource: draft.resource,
        access: true,
        rules,
      };
      if (isPageConfig) {
        payload.metadata = normalizeMetadataIn(draft.metadata);
      }
      if (draft.isNew || !draft.permission_id) {
        await axios.post('/api/permissions', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const putBody = { access: true, rules };
        if (isPageConfig) putBody.metadata = normalizeMetadataIn(draft.metadata);
        await axios.put(`/api/permissions/${draft.permission_id}`, putBody, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      await fetchData();
      closeEditor();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async () => {
    if (!draft?.permission_id) {
      closeEditor();
      return;
    }
    const msg =
      draft.type === 'capability'
        ? `Remove feature rule "${draft.resource}"?`
        : draft.type === 'page_configuration'
          ? `Remove this audience for "${draft.resource}"?`
          : `Remove route rule for "/${draft.resource}"? The route will be open to everyone.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/permissions/${draft.permission_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchData();
      closeEditor();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove');
    } finally {
      setSaving(false);
    }
  };

  const pickPageToAdd = (path) => {
    setAddModalOpen(false);
    openPageEditor(path);
  };

  // Strip leading slashes, trim, collapse whitespace -> dash. Resource is stored without
  // a leading slash, matching how the server normalizes `location.pathname.slice(1)`.
  const normalizeCustomPath = (raw) =>
    String(raw || '')
      .trim()
      .replace(/^\/+/, '')
      .replace(/\s+/g, '-');

  const customPathPreview = normalizeCustomPath(customPagePath);
  const customPathAlreadyExists =
    !!customPathPreview && permissions.some((p) => p.type === 'page' && p.resource === customPathPreview);

  const submitCustomPath = () => {
    if (!customPathPreview || customPathAlreadyExists) return;
    setAddModalOpen(false);
    openPageEditor(customPathPreview);
  };

  if (loading) return <LoadingSpinner />;

  const showBottomPanel = Boolean(selectedPageKey);

  return (
    <div style={{ ...s.page, ...(showBottomPanel ? s.pageWithPanel : {}) }}>
      <header style={s.headerRow}>
        <div>
          <h1 style={s.title}>Permissions</h1>
          <p style={s.lead}>
            Select a page to review route, feature, and audience rules. Dynamic routes (e.g.{' '}
            <code style={s.inlineCodeSm}>/saf-form/&lt;eventId&gt;</code>) match the longest{' '}
            <code style={s.inlineCodeSm}>resource</code> prefix.
          </p>
        </div>
        <div style={s.headerTools}>
          <button
            type="button"
            style={s.btnPlus}
            onClick={() => setAddModalOpen(true)}
            title="Add route rule"
            aria-haspopup="dialog"
          >
            +
          </button>
        </div>
      </header>

      {error && (
        <div style={s.banner} role="alert">
          <span style={s.bannerText}>{error}</span>
          <button type="button" style={s.bannerDismiss} onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {pageKeys.length === 0 ? (
        <div style={s.emptyCard}>
          No permissions yet. Use + to add a route rule, or create feature rules from a route that has them.
        </div>
      ) : (
        <div style={s.widgetGrid}>
          {pageKeys.map((pageKey) => {
            const stats = pageStatsForKey(pageKey, permissions, homeWidgetRows);
            const isSelected = selectedPageKey === pageKey;
            const meta = pageWidgetMetaLine(stats);

            return (
              <button
                key={pageKey}
                type="button"
                className={`pam-page-widget${isSelected ? ' pam-page-widget--selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openPagePanel(pageKey)}
                style={{
                  ...s.pageWidget,
                  ...(isSelected ? s.pageWidgetSelected : {}),
                }}
                title={`${pageDisplayName(pageKey)} — /${pageKey} — ${meta}`}
                aria-pressed={isSelected}
              >
                <span
                  style={{
                    ...s.pageWidgetTitle,
                    ...(isSelected ? s.pageWidgetTitleSelected : {}),
                  }}
                >
                  {pageDisplayName(pageKey)}
                </span>
                <span
                  style={{
                    ...s.pageWidgetMeta,
                    ...(isSelected ? s.pageWidgetMetaSelected : {}),
                  }}
                >
                  {meta}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {selectedPageKey && selectedPageStats && (
        <div
          style={s.bottomPanel}
          role="region"
          aria-label={`Permissions for ${pageDisplayName(selectedPageKey)}`}
        >
          <div style={s.bottomPanelInner}>
            <div style={s.bottomPanelHeader}>
              <div>
                <div style={s.bottomPanelTitleRow}>
                  <h2 style={s.bottomPanelTitle}>{pageDisplayName(selectedPageKey)}</h2>
                  <a
                    href={getPagePath(selectedPageKey)}
                    className="pam-page-open"
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open ${pageDisplayName(selectedPageKey)} in new tab`}
                    aria-label={`Open ${pageDisplayName(selectedPageKey)} in new tab`}
                  >
                    <IconPageOpen />
                  </a>
                </div>
                <p style={s.bottomPanelSubtitle}>
                  <code style={s.inlineCodeSm}>/{selectedPageKey}</code>
                  {' · '}
                  {pageWidgetMetaLine(selectedPageStats)}
                  {' · '}
                  {selectedPageStats.routeRestricted ? 'Route restricted' : 'Route open'}
                </p>
              </div>
              <div style={s.bottomPanelHeaderActions}>
                <button
                  type="button"
                  onClick={closePagePanel}
                  style={s.bottomPanelClose}
                  title="Close panel"
                  aria-label="Close panel"
                >
                  ×
                </button>
              </div>
            </div>
            <p className="pam-bottom-panel-desc">{pageDescription(selectedPageKey)}</p>
            <div className="pam-bottom-panel-body">
              <PagePermissionsDetail
                pageKey={selectedPageKey}
                permissions={permissions}
                homeWidgetRows={homeWidgetRows}
                schoolById={schoolById}
                associations={associations}
                hoverListRoute={hoverListRoute}
                setHoverListRoute={setHoverListRoute}
                hoverListCap={hoverListCap}
                setHoverListCap={setHoverListCap}
                openPageEditor={openPageEditor}
                openCapabilityEditor={openCapabilityEditor}
                openPageConfigCreate={openPageConfigCreate}
                openPageConfigEditor={openPageConfigEditor}
              />
            </div>
          </div>
        </div>
      )}

      {addModalOpen &&
        createPortal(
          <div
            style={s.addModalOverlay}
            onClick={() => setAddModalOpen(false)}
            role="presentation"
          >
            <div
              style={s.addModalCard}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="pam-add-modal-title"
              onKeyDown={(e) => {
                if (e.key === 'Escape') setAddModalOpen(false);
              }}
            >
              <div style={s.addModalHeader}>
                <h2 id="pam-add-modal-title" style={s.addModalTitle}>
                  Add a route rule
                </h2>
                <button
                  type="button"
                  style={s.addModalClose}
                  onClick={() => setAddModalOpen(false)}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div style={s.addModalSection}>
                <label style={s.addModalSectionLabel} htmlFor="pam-custom-route-input">
                  Custom route
                </label>
                <div style={s.addMenuCustomRow}>
                  <span style={s.addMenuCustomPrefix}>/</span>
                  <input
                    id="pam-custom-route-input"
                    ref={customRouteInputRef}
                    type="text"
                    placeholder="any-path/sub"
                    value={customPagePath}
                    onChange={(e) => setCustomPagePath(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitCustomPath();
                      }
                    }}
                    style={s.addMenuCustomInput}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    style={{
                      ...s.addModalGoBtn,
                      ...((!customPathPreview || customPathAlreadyExists) ? s.btnPrimaryDisabled : {}),
                    }}
                    disabled={!customPathPreview || customPathAlreadyExists}
                    onClick={submitCustomPath}
                    title="Open editor for this route"
                  >
                    Add
                  </button>
                </div>
                <div style={s.addMenuHint}>
                  {customPathPreview ? (
                    customPathAlreadyExists ? (
                      <span style={s.addMenuHintWarn}>
                        <code style={s.addMenuCode}>/{customPathPreview}</code> already has a rule
                        — edit it from the list below.
                      </span>
                    ) : (
                      <>
                        Will create a route rule for{' '}
                        <code style={s.addMenuCode}>/{customPathPreview}</code>. Dynamic segments
                        like <code style={s.addMenuCode}>/{customPathPreview}/&lt;id&gt;</code>{' '}
                        are covered automatically.
                      </>
                    )
                  ) : (
                    <>
                      Use this for routes not in the curated list (legacy, one-off, or new pages).
                      Leading <code style={s.addMenuCode}>/</code> is implied.
                    </>
                  )}
                </div>
              </div>

              <div style={s.addModalDivider}>
                <span style={s.addModalDividerText}>or pick a known route</span>
              </div>

              <div style={s.addModalListWrap}>
                {pagesWithoutRouteRule.length === 0 ? (
                  <div style={s.addMenuEmpty}>Every known route already has a rule.</div>
                ) : (
                  <div style={s.addModalList}>
                    {pagesWithoutRouteRule.map((path) => (
                      <button
                        key={path}
                        type="button"
                        style={s.addModalListItem}
                        onClick={() => pickPageToAdd(path)}
                      >
                        <code style={s.addMenuCode}>/{path}</code>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {editorModal && draft && (
        <div style={s.overlay} role="presentation">
          <div
            style={{
              ...s.editorDialog,
              ...(editorModal.kind === 'page_configuration' ? s.editorDialogHome : {}),
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="pam-editor-title"
          >
            <h2 id="pam-editor-title" style={s.dialogTitle}>
              {editorModal.kind === 'page'
                ? `/${editorModal.pageKey}`
                : editorModal.kind === 'page_configuration'
                  ? KNOWN_PAGE_CONFIGURATION_RESOURCES[editorModal.resource]?.label ||
                    editorModal.resource
                  : capabilityLabel(editorModal.resource)}
            </h2>
            {editorModal.kind === 'page' ? (
              <p style={s.dialogSubtitle}>
                Who may load this route? First matching rule applies; access is allowed when the rule matches.
              </p>
            ) : editorModal.kind === 'page_configuration' ? (
              <p style={s.dialogSubtitle}>
                Audience rules below decide who this configuration applies to. Among all audiences, the most specific
                match wins (associations &gt; emails &gt; specific schools &gt; role-only / global).
              </p>
            ) : (
              <p style={s.dialogSubtitle}>
                <code style={s.inlineCodeSm}>{editorModal.resource}</code> — same rule logic as routes.
              </p>
            )}

            <div style={s.editorBody}>
              <div style={s.editorSectionLabel}>Audience</div>
              <RulesEditor
                draft={draft}
                setDraft={setDraft}
                schools={schools}
                associations={associations}
                viewerIsOwner={viewerIsOwner}
                ownerEmails={ownerEmails}
                onOwnerGuardError={setError}
              />
              {editorModal.kind === 'page_configuration' &&
                editorModal.resource === HOME_WIDGETS_RESOURCE && (
                  <>
                    <div style={s.editorSectionLabel}>Configuration</div>
                    <WidgetsMetadataEditor draft={draft} setDraft={setDraft} />
                  </>
                )}
            </div>

            {editorModal.kind === 'page' && (() => {
              const pageKey = editorModal.pageKey;
              const capsKnown = knownCapabilitiesForPage(pageKey);
              const capRows = capabilityRowsForPage(pageKey, permissions);
              return (
                <div style={s.modalExtra}>
                  {capsKnown.length === 0 && capRows.length === 0 ? (
                    <p style={s.mutedNote}>This page has no controlled capabilities yet.</p>
                  ) : (
                    <>
                      {capsKnown.map((capRes) => {
                        const row = capRows.find((p) => p.resource === capRes);
                        return (
                          <button
                            key={capRes}
                            type="button"
                            style={{
                              ...s.modalExtraRow,
                              ...(hoverCapRes === capRes ? s.modalExtraRowHover : {}),
                            }}
                            onClick={() => openCapabilityEditor(capRes)}
                            onMouseEnter={() => setHoverCapRes(capRes)}
                            onMouseLeave={() => setHoverCapRes(null)}
                          >
                            <span style={s.modalExtraTitle}>{capabilityLabel(capRes)}</span>
                            <span style={s.modalExtraMeta}>
                              {row ? summarizeRules(row.rules, schoolById, associations) : 'Not configured'}
                            </span>
                          </button>
                        );
                      })}
                      {capRows
                        .filter((p) => !capsKnown.includes(p.resource))
                        .map((p) => (
                          <button
                            key={p.resource}
                            type="button"
                            style={{
                              ...s.modalExtraRow,
                              ...(hoverCapRes === p.resource ? s.modalExtraRowHover : {}),
                            }}
                            onClick={() => openCapabilityEditor(p.resource)}
                            onMouseEnter={() => setHoverCapRes(p.resource)}
                            onMouseLeave={() => setHoverCapRes(null)}
                          >
                            <span style={s.modalExtraTitle}>{capabilityLabel(p.resource)}</span>
                            <span style={s.modalExtraMeta}>
                              {summarizeRules(p.rules, schoolById, associations)}
                            </span>
                          </button>
                        ))}
                    </>
                  )}
                </div>
              );
            })()}

            <div style={s.dialogActions}>
              {!draft.isNew && draft.permission_id && (
                <button type="button" style={s.btnDangerGhost} onClick={deleteDraft} disabled={saving}>
                  Remove
                </button>
              )}
              <div style={s.dialogActionsRight}>
                <button type="button" style={s.btnGhost} onClick={closeEditor} disabled={saving}>
                  Cancel
                </button>
                <button type="button" style={s.btnPrimary} onClick={saveDraft} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const brand = '#1D4E89';

const colors = {
  text: '#0f172a',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  danger: '#b91c1c',
  dangerBorder: '#fecaca',
  warning: '#b45309',
};

const fontStack =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif';
const monoStack =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

const s = {
  page: {
    maxWidth: 880,
    margin: '20px auto',
    padding: '16px',
    backgroundColor: colors.surface,
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontFamily: fontStack,
    color: colors.text,
  },
  pageWithPanel: {
    paddingBottom: '50vh',
  },
  headerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  headerTools: { display: 'flex', alignItems: 'center', gap: 8 },
  title: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: brand,
    margin: 0,
    letterSpacing: '-0.01em',
  },
  lead: {
    margin: '4px 0 0 0',
    color: colors.textSecondary,
    fontSize: '0.78rem',
    lineHeight: 1.45,
    maxWidth: 640,
  },
  btnPlus: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.textSecondary,
    fontSize: '1.05rem',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 2,
    fontFamily: fontStack,
  },
  addMenuEmpty: { padding: '12px 6px', fontSize: '0.78rem', color: colors.textMuted },
  addMenuCode: {
    fontSize: '0.82rem',
    color: colors.text,
    fontFamily: monoStack,
  },
  addMenuCustomRow: { display: 'flex', alignItems: 'stretch', gap: 0 },
  addMenuCustomPrefix: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 9,
    paddingRight: 5,
    background: colors.surfaceHover,
    border: `1px solid ${colors.border}`,
    borderRight: 'none',
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    color: colors.textSecondary,
    fontFamily: monoStack,
    fontSize: '0.85rem',
  },
  addMenuCustomInput: {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    border: `1px solid ${colors.border}`,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    fontFamily: monoStack,
    fontSize: '0.85rem',
    outline: 'none',
    color: colors.text,
    background: colors.surface,
  },
  addMenuHint: {
    marginTop: 8,
    fontSize: '0.72rem',
    color: colors.textSecondary,
    lineHeight: 1.45,
  },
  addMenuHintWarn: { color: colors.warning },
  addModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
  },
  addModalCard: {
    background: colors.surface,
    borderRadius: 12,
    width: '100%',
    maxWidth: 460,
    maxHeight: 'min(86vh, 720px)',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.3)',
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: fontStack,
  },
  addModalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 4px',
  },
  addModalTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: colors.text,
    letterSpacing: '-0.01em',
  },
  addModalClose: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: colors.textSecondary,
    fontSize: '1.15rem',
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addModalSection: {
    padding: '6px 16px 12px',
  },
  addModalSectionLabel: {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
  },
  addModalGoBtn: {
    marginLeft: 6,
    padding: '6px 12px',
    border: `1px solid ${colors.text}`,
    background: colors.text,
    color: '#fff',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
  },
  addModalDivider: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 16px 6px',
  },
  addModalDividerText: {
    background: colors.surface,
    fontSize: '0.64rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
    padding: '0 6px',
    position: 'relative',
    zIndex: 1,
  },
  addModalListWrap: {
    padding: '0 16px 14px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  addModalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: 4,
    background: colors.surface,
  },
  addModalListItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    padding: '5px 8px',
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: fontStack,
  },
  btnPrimaryDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    background: '#fef2f2',
    border: `1px solid ${colors.dangerBorder}`,
    color: colors.danger,
    padding: '7px 10px',
    borderRadius: 8,
    marginBottom: 10,
    fontSize: '0.8rem',
  },
  bannerText: { flex: 1 },
  bannerDismiss: {
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    fontSize: '1.05rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
    opacity: 0.7,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  emptyCard: {
    padding: '24px',
    textAlign: 'center',
    color: colors.textMuted,
    fontSize: '0.82rem',
    border: `1px dashed ${colors.border}`,
    borderRadius: 10,
    background: colors.surfaceAlt,
  },

  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))',
    gap: 8,
  },
  pageWidget: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 6,
    minHeight: 64,
    height: 64,
    padding: '8px 10px',
    border: '1px solid #eceef1',
    borderRadius: 8,
    background: '#fafafa',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: fontStack,
    outline: 'none',
    transition: 'border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease',
  },
  pageWidgetSelected: {
    borderColor: brand,
    background: '#eff6ff',
    boxShadow: `0 0 0 1px rgba(29, 78, 137, 0.22)`,
  },
  pageWidgetTitle: {
    fontSize: '0.7rem',
    fontWeight: 500,
    color: '#52525b',
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    width: '100%',
  },
  pageWidgetTitleSelected: {
    color: brand,
    fontWeight: 600,
  },
  pageWidgetMeta: {
    fontSize: '0.62rem',
    fontWeight: 400,
    color: '#a1a1aa',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  },
  pageWidgetMetaSelected: {
    color: brand,
  },

  bottomPanel: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: '50vh',
    bottom: 0,
    zIndex: 1200,
    background: 'rgba(255, 255, 255, 0.96)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderTop: '1px solid #e5e7eb',
    boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    flexDirection: 'column',
  },
  bottomPanelInner: {
    maxWidth: 880,
    margin: '0 auto',
    width: '100%',
    height: '100%',
    padding: '12px 20px 14px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
  bottomPanelHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexShrink: 0,
  },
  bottomPanelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  bottomPanelTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    color: colors.text,
  },
  bottomPanelSubtitle: {
    margin: '2px 0 0',
    fontSize: '0.72rem',
    color: colors.textMuted,
  },
  bottomPanelHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  bottomPanelClose: {
    width: 28,
    height: 28,
    border: 'none',
    background: 'transparent',
    color: colors.textMuted,
    cursor: 'pointer',
    fontSize: '1.25rem',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    fontFamily: fontStack,
  },

  pageCardBody: { display: 'block', paddingBottom: 8 },
  chipMuted: {
    display: 'inline-block',
    padding: '1px 7px',
    fontSize: '0.68rem',
    fontWeight: 600,
    color: colors.textSecondary,
    background: colors.surfaceHover,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  },

  // Rows inside a page card
  rowBtn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    border: 'none',
    borderTop: `1px solid ${colors.surfaceHover}`,
    background: colors.surface,
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    fontFamily: fontStack,
    color: colors.text,
  },
  rowBtnHover: { background: colors.surfaceAlt },
  rowIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    marginTop: 1,
    borderRadius: 6,
    background: colors.surfaceAlt,
    color: colors.textSecondary,
    flexShrink: 0,
  },
  rowBody: { display: 'block', minWidth: 0, flex: 1 },
  rowEyebrow: {
    display: 'block',
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
    marginBottom: 2,
  },
  rowTitle: {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: colors.text,
    marginBottom: 2,
  },
  rowTitleSub: { fontWeight: 500, color: colors.textMuted },
  rowPreview: {
    display: 'block',
    fontSize: '0.76rem',
    color: colors.textSecondary,
    lineHeight: 1.45,
  },

  // Sub-section (Features / Audiences) inside a page card
  subSection: {
    padding: '8px 0 4px',
    borderTop: `1px dashed ${colors.border}`,
  },
  subSectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px 6px',
    gap: 8,
  },
  eyebrow: {
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
  },
  eyebrowCount: {
    fontSize: '0.66rem',
    fontWeight: 600,
    color: colors.textMuted,
    background: colors.surfaceHover,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: '0 6px',
  },
  subSectionHint: {
    margin: '0 12px 6px',
    fontSize: '0.76rem',
    color: colors.textMuted,
    fontStyle: 'italic',
  },
  ghostBtn: {
    padding: '3px 8px',
    background: 'transparent',
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
  },

  // Capability rows shown inside the page editor dialog
  modalExtra: {
    margin: '0 1.1rem 0.85rem',
    paddingTop: '0.65rem',
    borderTop: `1px solid ${colors.surfaceHover}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  mutedNote: { margin: 0, fontSize: '0.78rem', color: colors.textMuted },
  modalExtraRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 3,
    width: '100%',
    textAlign: 'left',
    padding: '0.5rem 0.6rem',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    background: colors.surface,
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    fontFamily: fontStack,
  },
  modalExtraRowHover: { background: colors.surfaceAlt },
  modalExtraTitle: { fontSize: '0.82rem', fontWeight: 600, color: colors.text },
  modalExtraMeta: { fontSize: '0.74rem', color: colors.textSecondary, lineHeight: 1.4 },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.32)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1300,
    padding: 16,
  },
  editorDialog: {
    background: colors.surface,
    borderRadius: 12,
    maxWidth: 520,
    width: '100%',
    maxHeight: 'min(90vh, 720px)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
    border: `1px solid ${colors.border}`,
    fontFamily: fontStack,
  },
  editorDialogHome: { maxWidth: 640 },
  widgetBlock: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: `1px dashed ${colors.border}`,
  },
  widgetBlockTitle: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: colors.textSecondary,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  widgetHint: {
    margin: '0 0 8px 0',
    fontSize: '0.7rem',
    color: colors.textMuted,
    lineHeight: 1.45,
  },
  widgetRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    marginBottom: 6,
  },
  widgetCard: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: 8,
    marginBottom: 8,
  },
  widgetTopRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
    marginBottom: 6,
  },
  widgetInput: {
    flex: '1 1 120px',
    minWidth: 0,
    padding: '5px 7px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.78rem',
    boxSizing: 'border-box',
    fontFamily: fontStack,
  },
  widgetPathInput: {
    width: '100%',
    padding: '5px 7px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.78rem',
    fontFamily: monoStack,
    boxSizing: 'border-box',
  },
  iconInput: {
    width: 48,
    flexShrink: 0,
    padding: '5px 6px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.95rem',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  widgetReorder: { display: 'flex', gap: 4, flexShrink: 0 },
  reorderBtn: {
    border: `1px solid ${colors.border}`,
    background: colors.surfaceAlt,
    color: colors.textSecondary,
    borderRadius: 6,
    width: 26,
    height: 26,
    cursor: 'pointer',
    fontSize: '0.8rem',
    lineHeight: 1,
  },
  widgetRemove: {
    border: `1px solid ${colors.dangerBorder}`,
    background: '#fee2e2',
    color: colors.danger,
    borderRadius: 6,
    width: 26,
    height: 26,
    cursor: 'pointer',
    fontSize: '0.95rem',
    lineHeight: 1,
    flexShrink: 0,
  },
  editorSectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: colors.textMuted,
    margin: '4px 0 6px',
    paddingTop: 2,
  },
  addWidgetBtn: {
    marginTop: 4,
    background: 'transparent',
    border: `1px dashed ${colors.borderStrong}`,
    color: colors.textSecondary,
    padding: '4px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.74rem',
    fontWeight: 600,
    fontFamily: fontStack,
  },
  dialogTitle: {
    margin: '1rem 1.1rem 0',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: colors.text,
    letterSpacing: '-0.01em',
  },
  dialogSubtitle: {
    margin: '4px 1.1rem 0',
    fontSize: '0.76rem',
    color: colors.textSecondary,
    lineHeight: 1.45,
  },
  inlineCodeSm: {
    background: colors.surfaceHover,
    padding: '1px 5px',
    borderRadius: 4,
    fontSize: '0.78em',
    fontFamily: monoStack,
    color: colors.text,
  },
  editorBody: {
    margin: '0.85rem 1.1rem',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  dialogActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: '0.75rem 1.1rem',
    borderTop: `1px solid ${colors.surfaceHover}`,
  },
  dialogActionsRight: { display: 'flex', gap: 8, marginLeft: 'auto' },
  btnGhost: {
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    padding: '5px 12px',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
  },
  btnPrimary: {
    background: colors.text,
    color: '#fff',
    border: `1px solid ${colors.text}`,
    padding: '5px 14px',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
  },
  btnDangerGhost: {
    background: 'transparent',
    border: `1px solid ${colors.dangerBorder}`,
    color: colors.danger,
    padding: '5px 10px',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: fontStack,
  },
  rulesStack: { display: 'flex', flexDirection: 'column', gap: 8 },
  ruleCard: {
    background: colors.surfaceAlt,
    padding: 10,
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
  },
  selectCompact: {
    padding: '4px 8px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.78rem',
    maxWidth: '100%',
    fontFamily: fontStack,
    background: colors.surface,
    color: colors.text,
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: colors.surface,
    padding: '2px 8px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: fontStack,
    color: colors.text,
  },
  chipProtected: {
    opacity: 0.72,
    background: colors.surfaceAlt,
    color: colors.textSecondary,
    cursor: 'not-allowed',
  },
  mutedSmall: { fontSize: '0.72rem', color: colors.textMuted, alignSelf: 'center' },
  inputRule: {
    width: '100%',
    padding: '6px 8px',
    marginTop: 8,
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.78rem',
    boxSizing: 'border-box',
    fontFamily: fontStack,
  },
  emailTextarea: {
    width: '100%',
    minHeight: 72,
    padding: '6px 8px',
    marginTop: 8,
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: '0.78rem',
    lineHeight: 1.45,
    fontFamily: fontStack,
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  addRule: {
    alignSelf: 'flex-start',
    background: 'transparent',
    border: `1px dashed ${colors.borderStrong}`,
    color: colors.textSecondary,
    padding: '4px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.76rem',
    fontWeight: 600,
    fontFamily: fontStack,
  },
  textDanger: {
    display: 'block',
    marginTop: 8,
    background: 'none',
    border: 'none',
    color: colors.danger,
    fontSize: '0.72rem',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    fontFamily: fontStack,
  },
};

export default PageAccessManager;
